import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import type { MusicTrack, MusicTrackUpdate } from '../../api/music';
import { StarRating } from './StarRating';

interface TrackEditDialogProps {
    track: MusicTrack | null;
    saving: boolean;
    onClose: () => void;
    onSave: (id: string, payload: MusicTrackUpdate) => void;
}

const emptyToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export const TrackEditDialog = ({ track, saving, onClose, onSave }: TrackEditDialogProps) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ title: '', artist: '', albumPath: '', source: '', license: '', sourceUrl: '' });
    const [rating, setRating] = useState(0);

    useEffect(() => {
        if (track) {
            setForm({
                title: track.title ?? '',
                artist: track.artist ?? '',
                albumPath: track.album_path ?? '',
                source: track.source ?? '',
                license: track.license ?? '',
                sourceUrl: track.source_url ?? '',
            });
            setRating(track.rating ?? 0);
        }
    }, [track]);

    const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

    const submit = () => {
        if (!track || !form.title.trim()) return;
        const albumPath = emptyToNull(form.albumPath);
        const payload: MusicTrackUpdate = {
            title: form.title.trim(),
            artist: emptyToNull(form.artist),
            source: emptyToNull(form.source),
            license: emptyToNull(form.license),
            source_url: emptyToNull(form.sourceUrl),
        };
        if (rating !== (track.rating ?? 0)) {
            payload.rating = rating;
        }
        if (albumPath !== (track.album_path ?? null)) {
            payload.album_path = albumPath;
            // the album name is the leaf of the path
            payload.album = albumPath ? (albumPath.split(/[\\/]+/).filter(Boolean).pop() ?? null) : null;
        }
        onSave(track.id, payload);
    };

    return (
        <Dialog open={track !== null} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('music.editTrack')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label={t('music.trackTitle')} value={form.title} onChange={set('title')} required fullWidth />
                    <TextField label={t('music.artist')} value={form.artist} onChange={set('artist')} fullWidth />
                    <TextField label={t('music.albumPath')} helperText={t('music.albumPathHint')} value={form.albumPath} onChange={set('albumPath')} fullWidth />
                    <TextField label={t('music.source')} value={form.source} onChange={set('source')} fullWidth />
                    <TextField label={t('music.license')} value={form.license} onChange={set('license')} fullWidth />
                    <TextField label={t('music.sourceUrl')} value={form.sourceUrl} onChange={set('sourceUrl')} fullWidth />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">{t('music.rating')}</Typography>
                        <StarRating value={rating} onChange={setRating} size="medium" />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('music.cancel')}</Button>
                <Button variant="contained" onClick={submit} disabled={saving || !form.title.trim()}>{t('music.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};
